import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Box,
    Divider
} from '@mui/material';
import {
    School as SchoolIcon,
    Person as PersonIcon,
    LocalOffer as OfferIcon,
    Assignment as EnrollmentIcon
} from '@mui/icons-material';
import { Organization, OrganizationStats } from '../../types/organization';
import api from '../../services/api';

interface OrganizationDetailsProps {
    open: boolean;
    onClose: () => void;
    organization: Organization | null;
}

const OrganizationDetails: React.FC<OrganizationDetailsProps> = ({ open, onClose, organization }) => {
    const [stats, setStats] = useState<OrganizationStats | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (organization && open) {
            const fetchStats = async () => {
                setLoading(true);
                try {
                    if (organization) {
                        const res = await api.get(`/api/organizations/${organization._id}/stats`);
                        setStats(res.data);
                    }
                } catch (err) {
                    console.error('Error fetching organization stats', err);
                } finally {
                    setLoading(false);
                }
            };
            fetchStats();
        } else {
            setStats(null);
        }
    }, [organization, open]);

    if (!organization) return null;

    const StatCard = ({ title, value, icon, color }: { title: string, value: number, icon: React.ReactNode, color: string }) => (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', textAlign: 'center' }}>
                <Box sx={{ color: color, mb: 1 }}>
                    {icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
            </CardContent>
        </Card>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    {organization.logoUrl && (
                        <Box
                            component="img"
                            src={organization.logoUrl}
                            alt="Logo"
                            sx={{ width: 32, height: 32, mr: 2, objectFit: 'contain' }}
                        />
                    )}
                    {organization.name}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom>Informations Générales</Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">Sous-domaine</Typography>
                            <Typography variant="body1">{organization.subdomain}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">Date de création</Typography>
                            <Typography variant="body1">
                                {new Date(organization.createdAt).toLocaleDateString()}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>Statistiques</Typography>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : stats ? (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Apprenants"
                                value={stats.learnersCount}
                                icon={<SchoolIcon fontSize="large" />}
                                color="#3b82f6"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Formateurs"
                                value={stats.trainersCount}
                                icon={<PersonIcon fontSize="large" />}
                                color="#10b981"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Offres Actives"
                                value={stats.activeOffersCount}
                                icon={<OfferIcon fontSize="large" />}
                                color="#f59e0b"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Inscriptions Actives"
                                value={stats.activeEnrollmentsCount}
                                icon={<EnrollmentIcon fontSize="large" />}
                                color="#8b5cf6"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box mt={2} textAlign="center">
                                <Typography variant="caption" color="text.secondary">
                                    Total des inscriptions (historique) : {stats.totalEnrollmentsCount}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Impossible de charger les statistiques.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrganizationDetails;
